/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FontWeights, getTheme } from '@fluentui/react/lib/Styling';
import { NeutralColors } from '@fluentui/theme';

export const entityColors = {
    defaultColor: NeutralColors.gray120,
    depth1Color: '#0078D4',
    depth2Color: '#E03315',
    depth3Color: '#00818A',
    depth4Color: '#E3008C',
    depth5Color: '#917EDB',
    noneColor: getTheme().palette.neutralSecondaryAlt
};

export const accessibleEntityColorsWithWhiteBackground = [
    '#00758F',
    '#B34F90',
    '#4F6BED',
    '#6D5700',
    '#286EA8',
    '#A43FB1',
    '#CC3595',
    '#0E7878',
    '#8764B8',
    '#9C663F',
    '#00566A',
    '#AE4689',
    '#4661D5',
    '#3A2F00',
    '#125D9A',
    '#972AA5',
    '#AC2D7E',
    '#006666',
    '#7959A5',
    '#8E562E',
    '#002E39',
    '#982570',
    '#3B52B4',
    '#1F1900',
    '#004E8C',
    '#881798',
    '#7F215D',
    '#005C5C',
    '#664B8C',
    '#814E29',
    '#00191E',
    '#88105E',
    '#2C3C85',
    '#00457E',
    '#7C158A',
    '#441232',
    '#004E4E',
    '#4B3867',
    '#6D4123',
    '#77004D',
    '#172047',
    '#003B6B',
    '#691174',
    '#003939',
    '#281E37',
    '#50301A',
    '#5B003B',
    '#002B4F',
    '#4D0D56',
    '#001F1F',
    '#2B1A0E',
    '#43002C',
    '#00172A',
    '#29072E',
    '#240017'
];

/**
 * Defines all the theme settings for the website components.
 *
 * Note: Please keep the keys sorted in alphabetical order
 */
export const buildTheme = () => {
    const globalHeaderHeight = '40px';
    const globalSubHeaderHeight = '40px';
    const sidebarWidthCollapsed = '50px';
    const sidebarWidthExpanded = '240px';
    const zIndices = {
        header: 1,
        sidePanel: 2,
        toaster: 1000001,
        skipLink: 1000001,
        loadingPage: 9999999,
        modalContentOverlay: 1,
        confusionLink: 1,
        extractionSamplingBar: 1,
        classificationTopPane: 1
    };
    const { spacing, fonts, palette, effects } = getTheme();
    const monoSpacedFont = 'Monaco, Consolas, "Courier New", monospace;';

    return {
        AdvancedOptionsPane: {
            backgroundColor: NeutralColors.gray10,
            titleTextColor: NeutralColors.black,
            emptyTextColor: NeutralColors.gray130
        },
        AppInfoCard: {
            descriptionMaxWidth: '500px'
        },
        Arrow: {
            buttonBorderRadius: '50%',
            buttonColor: palette.black,
            hoveredButtonBackground: NeutralColors.gray40
        },
        AudioPlayer: {
            graySliderColor: '#e9e9e9',
            mainSliderColor: palette.themePrimary
        },
        AutoLabelingModal: {
            warningColor: palette.yellow,
            secondaryTextColor: palette.neutralSecondary
        },
        AutotagJobTableColumn: {
            fontSize: fonts.mediumPlus.fontSize
        },
        AutotagTriggerModalBase: {
            borderColor: palette.neutralQuaternaryAlt
        },
        AzureContainerBrowsingModal: { borderColor: palette.neutralTertiary },
        AzureCsAccountsPage: {
            instructionsMaxWidth: '1250px'
        },
        AzureCsAccountSelectModal: {
            buttonColor: palette.accent,
            accountInfoFontWeight: FontWeights.bold
        },
        AzureCsAccountCard: {
            resourceLabelMinWidth: '150px',
            themeFontSize: '12px',
            resourceLabelFontSize: '16px',
            resourceLabelFontWeight: 625,
            sectionWidth: '100%',
            cardInfoItemHeight: '32px',
            queryExampleWidth: '70%',
            queryExamplePaddingTop: spacing.s1,
            queryFieldMarginTop: spacing.s2,
            resourceTextLeftPadding: '10px'
        },
        AzureTenantList: {
            cardPadding: spacing.m,
            hoverBackgroundColor: palette.neutralLighter,
            selectedBackgroundColor: palette.neutralLight,
            cardBorderBottom: `1px solid ${palette.neutralQuaternaryAlt}`,
            sectionTitleFontWeight: 600,
            tenantTitleFontWeight: 500
        },
        AzureMLConnectionModal: {
            defaultTextColor: palette.black,
            borderColor: palette.neutralQuaternaryAlt
        },
        AzureMLTabConnectionFooter: {
            defaultTextColor: palette.black,
            borderColor: palette.neutralQuaternaryAlt
        },
        AzureMLConnectionPage: {
            warningColor: '#D83B01',
            successColor: palette.green,
            pendingColor: palette.neutralPrimary,
            disableColor: palette.neutralTertiaryAlt
        },
        OverviewPage: {
            calloutLinkColor: '#009eff'
        },
        Badge: {
            textColor: NeutralColors.gray130
        },
        BarTooltip: {
            fontSize: '13px',
            labelWidth: '200px',
            valueSpacing: '10px',
            backgroundColor: palette.white,
            fontWeight: FontWeights.regular,
            bodySpacing: '5px 30px 15px 10px',
            dataFontWeight: FontWeights.semibold,
            boxShadow: '0 0 2px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.16)'
        },
        BarTooltipHeader: {
            fontSize: '13px',
            lineHeight: '18px',
            spacing: '3px 10px',
            letterSpacing: '0.43px',
            textColor: palette.white,
            titleLabelFontWeight: 200,
            backgroundColor: palette.black,
            dataFontWeight: FontWeights.semibold
        },
        BatchBlade: {
            width: 1200,
            successColor: palette.green,
            failColor: palette.red,
            neutralColor: palette.accent
        },
        BatchBladeResults: {
            visualizationHeight: '400px',
            modelListWidth: '300px'
        },
        BatchTestStateCell: {
            actionButtonHeight: '16px',
            successColor: palette.green,
            failColor: palette.red
        },
        BatchTestResultGraph: {
            linkZIndex: zIndices.confusionLink,
            confusionLinkFontWeight: 600,
            positiveColor: palette.green,
            negativeColor: palette.red
        },
        BatchTestResultItemStats: {
            statFontWeight: 500
        },
        BasicModal: {
            defaultMaxHeight: '90vh',
            overlayColor: 'rgba(255,255,255,0.5)',
            progressBarPadding: '1px',
            modalContentOverlay: zIndices.modalContentOverlay,
            subTextColor: palette.black,
            subTextMargin: spacing.s1,
            modalMaxWidth: '95%'
        },
        Blade: {
            contentPadding: spacing.m,
            closeIconSize: fonts.medium.fontSize,
            closeIconDarkThemeColor: palette.neutralLighterAlt,
            headerHeight: '40px',
            hoveredCloseButtonBackground: 'rgba(235, 235, 235, 0.5)'
        },
        BladeHeader: {
            iconFontSize: '12px',
            headerLineHeight: '16px',
            closeIconColor: palette.black,
            headerSeparatorMargin: '1px 0',
            headerSpacing: '9px 0 9px 16px',
            boldFontWeight: FontWeights.semibold
        },
        BlankPage: {
            pageBackgroundColor: palette.white,
            pageZIndex: zIndices.loadingPage
        },
        Brush: {
            ...entityColors,
            containerBackground: palette.neutralSecondary,
            containerHeight: '28px',
            containerShadow: effects.elevation16,
            containerBorderRadius: '2px',
            containerTransition: 'opacity 100ms linear',
            iconSize: '12px',
            iconMargin: '8px 12px',
            iconColor: palette.white,
            splitterBackground: palette.neutralTertiary,
            circleSize: '12px',
            circleBorder: `1px solid ${palette.white}`,
            textMargin: '0px 12px 0 4px',
            zIndex: zIndices.toaster,
            textColor: palette.white,
            textSize: '12px'
        },
        CallCenterDemoPage: { borderColor: '#CCCCCC' },
        CallCenterSamplesSection: {
            iconFillColor: palette.white,
            audioIconBackground: '#773ADC',
            transcriptIconBackground: '#F78D1E'
        },
        Callout: {
            buttonBorderRadius: '50%',
            calloutBackground: palette.neutralDark,
            buttonBackground: NeutralColors.gray30,
            buttonColor: palette.black,
            hoveredButtonBackground: NeutralColors.gray40,
            darkTextColor: palette.white,
            lightTextColor: palette.black,
            lightBackgroundColor: palette.white,
            darkBackgroundColor: palette.neutralPrimary
        },
        Card: {
            minCardWidth: '340px',
            cardColor: '#fafafa',
            fontWeight: FontWeights.semibold,
            separatorLineColor: '#cccccc',
            padding: '12px 16px',
            boxShadow: '0 0 2px rgba(0, 0, 0, 0.08), 0 2px 2px rgba(0, 0, 0, 0.16)'
        },
        Carousel: {
            backgroundColor: palette.white
        },
        Charts: {
            minChartWidth: '300px',
            minChartHeight: '320px',
            xAxisTextColor: palette.neutralPrimary
        },
        ChatBlade: {
            utteranceBackgroundColor: palette.accent,
            selectedUtteranceBackgroundColor: palette.black,
            utteranceTextColor: palette.white,
            utterancePadding: `${spacing.s1} 12px`
        },
        CluModelDetailsPage: {
            warningLinkColor: '#006cbe',
            warningIconColor: palette.yellow
        },
        CluIntentsTable: {
            errorIconColor: palette.red
        },
        CluIntentCreationModal: {
            errorIconColor: palette.red
        },
        CluDataDistribution: {
            testingIntentColor: '#8764B8',
            trainingIntentsColor: '#4794FF',
            testingEntitiesInTagsColor: '#502006',
            trainingEntitiesInTagsColor: '#00A2AD',
            testingEntitiesInUtterancesColor: '#EF6950',
            trainingEntitiesInUtterancesColor: palette.themePrimary,
            borderSpecs: `1px solid ${palette.neutralQuaternaryAlt}`
        },
        CluEntityCombinationOnboardingModal: {
            defaultColor: palette.white,
            slideTextColor: palette.neutralSecondary,
            slidePrimaryTextColor: palette.themePrimary,
            slideHeaderBackgroundColor: palette.themeLight
        },
        CluEntityCombinationOptionDetails: {
            boxShadow: effects.elevation4,
            borderColor: palette.neutralQuaternary,
            imageBackgroundColor: palette.neutralLighter
        },
        CluEditEntityPane: {
            sidebarWidthCollapsed,
            sidebarWidthExpanded,
            backgroundColor: 'white',
            globalHeaderHeight
        },
        CluEntityCombinationOptionCard: {
            borderColor: palette.neutralQuaternary,
            selectedBorderColor: palette.themePrimary,
            boxShadow: effects.elevation4,
            buttonColor: palette.themePrimary
        },
        CluEntityCapabilityCollapsableCard: {
            borderColor: palette.neutralLighter
        },
        CluEntityListCapabilityTable: {
            borderColor: palette.neutralLighter
        },
        CluEntityRegexCapabilityTable: {
            borderColor: palette.neutralLighter
        },
        CluEntityPrebuiltCapabilityTable: {
            borderColor: palette.neutralLighter,
            secondaryColor: '#616161',
            hoveringColor: palette.neutralLighter,
            focussingColor: palette.neutralQuaternaryAlt
        },
        CluTestSetDetailsPage: {
            disabledIconBtnColor: `inherit !important`,
            annotationColor: '#D88716',
            tableBorderColor: palette.neutralLighter
        },
        CluEntitySublistInEditMode: {
            borderColor: palette.neutralLighter,
            secondaryColor: palette.neutralSecondary,
            hoveringColor: palette.neutralLighter,
            focussingColor: palette.neutralQuaternaryAlt
        },
        CluEntityRegexInEditMode: {
            borderColor: palette.neutralLighter,
            secondaryColor: palette.neutralSecondary,
            hoveringColor: palette.neutralLighter,
            focussingColor: palette.neutralQuaternaryAlt
        },
        CluMigrationModalBase: {
            buttonColor: palette.accent,
            borderColor: palette.neutralQuaternaryAlt,
            defaultTextColor: palette.black
        },
        CluMigrationModalSelectLuisApplicationsScreen: {
            hoveringColor: palette.neutralLighter,
            focusingColor: palette.neutralQuaternaryAlt,
            warningColor: palette.yellowDark
        },
        CluUtteranceCommandBar: {
            borderColor: palette.neutralLight,
            dirtyColor: palette.redDark,
            inProgressColor: palette.yellowDark,
            syncedColor: palette.green
        },
        CluUtteranceTable: {
            gptColor: '#6F37AE',
            borderColor: palette.neutralLight,
            secondaryTextColor: palette.neutralSecondary,
            editInputBackgroundColor: palette.neutralLight
        },
        CluUtteranceRenderer: {
            labelerMargin: '8px 6px 8px 0px',
            rejectButtonColor: 'rgba(168, 0, 0, 1)',
            acceptButtonColor: 'rgba(16, 124, 16, 1)'
        },
        CluProjectsTable: {
            projectTypeIconColor: palette.themePrimary
        },
        Collapsible: {
            actionColor: palette.accent,
            titleFontSize: fonts.medium.fontSize,
            iconSize: fonts.small.fontSize
        },
        CollapsedErrorMessage: {
            errorIconColor: palette.redDark
        },
        CompositeEntityChildTableCommandBar: {
            dropDownWidth: '272px'
        },
        Container: {
            side: spacing.l2,
            mainMaxWidth: '1600px',
            mainWidth: '90%',
            mainPadding: '3rem 0'
        },
        ConversationAppsTableCommandBar: {
            activeIconColor: palette.themeDarkAlt,
            importBtnBorderColor: palette.neutralTertiaryAlt
        },
        ConversationApplicationsTable: {
            appTypeIconColor: palette.themePrimary
        },
        CustomTextDataTaggingPage: {
            collapseIconColor: palette.black
        },
        CustomSentimentDataTaggingPage: {
            collapseIconColor: palette.black
        },
        LuisAppTrainButton: {
            greenIndicator: palette.greenDark,
            redIndicator: palette.red
        },
        ModelGuidanceCard: {
            arrowColor: palette.black,
            errorIconColor: '#E41323',
            warningIconColor: '#F2610C',
            checkMarkIconColor: '#018A39',
            boxShadow: effects.elevation4,
            emptyStateLinkColor: '#006cbe',
            borderColor: palette.neutralTertiaryAlt
        },
        CreateCompositeEntity: {
            addChildButtonHeight: '24px',
            addChildButtonMargin: spacing.s2,
            childRowContainerMargin: spacing.m,
            addedChildrenListMaxHeight: '190px'
        },
        ControlDatasetOptions: {
            boxShadow: effects.elevation4,
            buttonColor: palette.themePrimary,
            borderColor: palette.neutralQuaternary,
            invalidInputBorderColor: palette.redDark,
            selectedBorderColor: palette.themePrimary,
            disabledCardWrapperFontColor: '#737271'
        },
        ModelGuidanceModelDetailsCard: {
            recall: '#733569',
            f1Score: '#005EB8',
            precision: '#006875',
            emptyMetric: '#E1DFDD',
            boxShadow: effects.elevation4,
            neutral: palette.neutralQuaternaryAlt,
            borderColor: palette.neutralTertiaryAlt,
            secondaryTextColor: palette.neutralTertiary,
            trainingInProgressColor: palette.themePrimary,
            donutChartTitleFontWeight: FontWeights.semibold,
            donutChartSubtitleFontWeight: FontWeights.regular
        },
        CreateEntityModal: {
            optionPaddingLeft: spacing.l2,
            optionPaddingRight: spacing.l2,
            marginLeft: 'auto',
            mainContentPaddingLeft: '3px',
            optionTextTopAdjust: '3px',
            tutorialOptionTitleFontWeight: FontWeights.semibold,
            imageHeight: '180px'
        },
        CreateMLEntityScreen: {
            mlTreeContainerBackgroundColor: NeutralColors.gray10
        },
        ConfusionMatrixChart: {
            redPoint: palette.redDark,
            greenPoint: palette.tealLight
        },
        ConfusionMatrixColorValueScale: {
            cellColor0: '#D0D5DB',
            cellColor2: '#96ADCC',
            cellColor4: '#556B86',
            cellColor6: '#334D6B',
            cellColor8: '#001D3F',
            valueColor: palette.black
        },
        ContainerStatusCell: {
            secondTrackColor: '#C8C8C8',
            failedIconColor: palette.redDark,
            updateDisabledIconColor: palette.yellow,
            completedIconColor: palette.themePrimary,
            progressTrackerBackgroundColor: palette.neutralLight
        },
        ConversationFeaturePicker: {
            addTextColor: NeutralColors.gray130,
            toggleRequiredHoverBackground: NeutralColors.gray40,
            toggleRequiredActiveBackground: NeutralColors.gray50,
            toggleIconColor: NeutralColors.gray150,
            requiredColor: palette.accent
        },
        CopyButton: {
            iconColor: palette.themePrimary
        },
        CreateExtractorModal: {
            boldFont: FontWeights.semibold,
            featureSectionLineHeight: '16px'
        },
        CultureDropdown: {
            maxHeight: '200px'
        },
        CustomProjectsSection: {
            iconColor: palette.themePrimary,
            emptyStateColor: palette.neutralLighter,
            sectionBorderColor: palette.neutralLight
        },
        CustomTextAutotagTriggerModalSelectEntitiesScreen: {
            entitiesTagsColor: '#4794FF'
        },
        CustomTextBreadcrumb: {
            linkColor: palette.themePrimary
        },
        CustomTextClassPalette: {
            gptLabelColor: '#6F37AE',
            gptAcceptColor: '#008000',
            gptRejectColor: '#FF0000',
            separatorColor: '#EDEBE9',
            gptTextColor: palette.themePrimary,
            gptCheckBoxMarkColor: palette.white,
            selectedHighlighterButtonColor: 'rgb(237, 235, 233)',
            borderSpecs: `1px solid ${palette.neutralQuaternaryAlt}`
        },
        CustomTextChooseDataset: {
            borderColor: palette.neutralLight
        },
        CustomTextDataDistribution: {
            testingAcrossTagsColor: '#502006',
            trainingAcrossTagsColor: '#00A2AD',
            testingAcrossDocumentsColor: '#EF6950',
            trainingAcrossDocumentsColor: palette.themePrimary
        },
        CustomTextProjectsTable: {
            projectTypeIconColor: palette.themePrimary
        },
        CustomTextModelDetailsTable: {
            textColor: palette.neutralPrimary,
            completedColor: palette.green,
            failedColor: palette.red
        },
        CustomTextTestModelPage: {
            titleLineHeight: '56px',
            titleFontWeight: FontWeights.semibold,
            topBorderColor: palette.neutralLighter
        },
        CustomSentimentDataDistribution: {
            testingAcrossTagsColor: '#502006',
            trainingAcrossTagsColor: '#00A2AD',
            testingAcrossDocumentsColor: '#EF6950',
            trainingAcrossDocumentsColor: palette.themePrimary
        },
        CustomSentimentChooseDataset: {
            borderColor: palette.neutralLight
        },
        CustomSentimentBreadcrumb: {
            linkColor: palette.themePrimary
        },
        CustomSentimentProjectsTable: {
            projectTypeIconColor: palette.themePrimary
        },
        TestModelsFiltersBlade: {
            topSpacing: globalHeaderHeight,
            primaryColor: palette.themePrimary
        },
        CtFiltersPillContainer: {
            border: '1px solid rgb(200, 198, 196)',
            backgroundColor: 'rgb(243, 242, 241)',
            hoveredBackgroundColor: 'rgb(237, 235, 233)'
        },
        TestSetDetailsTable: {
            warningIconColor: '#F2610C',
            completedIconColor: '#018A39'
        },
        DataDistributionTable: {
            warningColor: '#A95D42',
            emptyStateLinkColor: '#006cbe'
        },
        ModelDetailsPage: {
            warningLinkColor: '#006cbe'
        },
        ModelGuidanceConfusionMatrix: {
            noneAndNoneCellColor: '#D0D5DB',
            tpCellTextColor: palette.white,
            noneAndNoneTextColor: '#092C47',
            mainCellBackgroundColor: '#B7C9D7',
            border: `1px solid ${palette.white}`,
            tpCellBackgroundColor: palette.themeDark,
            predictionBorderBottom: '1px solid #F1F1F1'
        },
        ConfusionMatrixCellColors: {
            cellColor0: '#D0D5DB',
            textColor0: '#092C47',
            cellColor2: '#96ADCC',
            textColor2: '#092C47',
            cellColor4: '#556B86',
            textColor4: '#EFF6FC',
            cellColor6: '#334D6B',
            textColor6: '#EFF6FC',
            cellColor8: '#001D3F',
            textColor8: '#EFF6FC'
        },
        DataDistributionGrid: {
            boxShadow: effects.elevation4,
            emptyStateLinkColor: '#006cbe',
            testingDocumentsColor: '#EF6950',
            testingInstancesColor: '#502006',
            trainingInstancesColor: '#00A2AD',
            trainingDocumentsColor: '#0078D4',
            borderColor: palette.neutralTertiaryAlt
        },
        DataDistributionBar: {
            warningColor: '#A95D42'
        },
        Dashboard: {
            xSmallFont: '12px',
            titleSpacing: '8px',
            labelFontSize: '13px',
            legendSpacing: '13px',
            sidebarWidth: '350px',
            largeFontSize: '24px',
            othersColor: '#767676',
            mediumFontSize: '15px',
            emptyRowHeight: '10px',
            dropDownWidth: '200px',
            legendDimension: '12px',
            correctColor: '#0078d7',
            largeLineHeight: '24px',
            unclearColor: '#f2610c',
            xLargeLineHeight: '32px',
            mediumLineHeight: '20px',
            legendBorder: '1px solid',
            incorrectColor: '#a80000',
            maxDropdownHeight: '300px',
            intentsListHeight: '230px',
            minSmallDonutWidth: '185px',
            largeLetterSpacing: '0.4px',
            ratioTextFieldWidth: '30px',
            dropdownSmallHeight: '25px',
            dropdownLargeHeight: '36px',
            dropdownBorderRadius: '3px',
            listItemLeftShifting: '30px',
            xLargeLetterSpacing: '0.5px',
            mediumLetterSpacing: '0.25px',
            intentListItemSpacing: '12px',
            nonTrainedCardHeight: '407px',
            ratioTextFieldPadding: '1px 0',
            boldFont: FontWeights.semibold,
            goodComparisonColor: '#599b00',
            badComparisonColor: palette.red,
            emptyLegendColor: palette.white,
            regionWrapperMaxHeight: '150px',
            publishCardLabelMinWidth: '80px',
            versionComparisonSpacing: '50px',
            publishCardLinkSpacing: '0.5rem',
            comparisonLegendItemWidth: '45px',
            semiboldFont: FontWeights.regular,
            comparisonLegendItemSpacing: '6px',
            intentsListItemSpacing: '8px 16px',
            defaultTextColor: palette.neutralPrimary,
            caretColor: palette.black,
            backgroundColor: palette.neutralLighterAlt,
            publishStatusLinksColor: palette.themeDarkAlt,
            detailedFaultyIntentLegendSpacing: '7.5rem',
            detailedFaultyIntentPercentLegendSpacing: '1rem',
            statsComparisonColor: palette.neutralSecondaryAlt,
            selectedIntentBackgroundColor: palette.themeLight,
            dropdownBorder: `1px solid ${palette.neutralTertiaryAlt}`,
            cardBoxShadowing: effects.elevation8,
            disabledColor: palette.neutralTertiary
        },
        DebugBlade: {
            textColor: palette.white,
            backgroundColor: palette.neutralDark
        },
        DemoHeader: {
            closeIconColor: palette.neutralPrimary,
            secondaryTextColor: palette.neutralSecondary
        },
        DemoPage: {
            ipEntityColor: '#00B24A',
            separatorColor: '#EDEBE9',
            urlEntityColor: '#1CC2B2',
            eventEntityColor: '#BE0151',
            skillEntityColor: '#C39F74',
            emailEntityColor: '#7CC102',
            personEntityColor: '#018A39',
            productEntityColor: '#E41323',
            addressEntityColor: '#FABE14',
            boxShadow: effects.elevation4,
            locationEntityColor: '#F2610C',
            dateTimeEntityColor: '#029DD4',
            quantityEntityColor: '#829196',
            requiredColor: palette.redDark,
            defaultTextColor: palette.black,
            personTypeEntityColor: '#A80088',
            phoneNumberEntityColor: '#CF0076',
            primaryColor: palette.themePrimary,
            organizationEntityColor: '#0166AC',
            rankEntityColor: palette.themeDark,
            hoverColor: palette.neutralLighter,
            disabledTextFieldTextColor: '#6D6C6B',
            secondaryTextColor: palette.neutralSecondary,
            sentenceCardBorderColor: palette.neutralLight,
            resultWrapperBorderColor: palette.neutralTertiaryAlt,
            extractiveCardColor: '#005A9E',
            abstractiveCardColor: '#FF7A00'
        },
        DeleteButton: {
            backgroundColor: '#a80000',
            hoverBackgroundColor: palette.redDark,
            activeBackgroundColor: palette.orange
        },
        DemoCommandBar: {
            topBorderColor: palette.neutralLighter
        },
        DemoTileSection: {
            sectionBorderColor: palette.neutralLight
        },
        DeployedModelsTable: {
            warningIconColor: '#E8825D',
            errorIconColor: palette.redDark,
            expiryIconLeftMargin: '12px',
            expiryIconRightMargin: '3px'
        },
        DocumentTranslationDemoPageInputStep: { borderColor: palette.neutralLight, listItemHoverBackgroundColor: palette.neutralLighter },
        DocumentTranslatorResultStep: {
            failureColor: palette.redDark,
            succeededColor: palette.green,
            notStartedColor: palette.yellow,
            borderColor: palette.neutralLight
        },
        DomainCard: {
            titleFontWeight: FontWeights.semibold
        },
        DomainsList: {
            searchBoxWidth: '50%',
            searchBoxBottomMargin: '32px'
        },
        DonutChart: {
            firstColor: palette.themePrimary,
            smallDonut: 145,
            largeDonut: 265,
            mediumDonut: 185,
            thirdColor: '#a80000',
            secondColor: '#f2610c',
            subtitleFontSize: '16px',
            subtitleLineHeight: '20px',
            largeTitleFontSize: '30px',
            mediumTitleFontSize: '24px',
            largeTitleLineHeight: '40px',
            mediumTitleLineHeight: '32px',
            largeTitleLetterSpacing: '0.3px',
            mediumTitleLetterSpacing: '0.4px',
            largeTitleFontWeight: FontWeights.semibold
        },
        DragAndDropInput: {
            iconColor: palette.neutralSecondary,
            borderColor: palette.neutralSecondaryAlt,
            borderColorHovered: palette.themePrimary,
            backgroundColor: palette.themeLighterAlt,
            linkColor: palette.blueMid
        },
        EntityBrush: {
            zIndex: zIndices.toaster,
            containerBackground: palette.white,
            containerShadow: effects.elevation16,
            iconColor: palette.themePrimary,
            splitterBackground: palette.neutralTertiary,
            circleBorder: `1px solid ${palette.white}`,
            textColor: palette.black
        },
        EntityPalette: {
            separatorColor: '#EDEBE9',
            entityDetailsColor: '#747372',
            gptTextColor: palette.themePrimary,
            secondaryColor: palette.neutralTertiaryAlt,
            selectedHighlighterButtonColor: 'rgb(237, 235, 233)',
            borderSpecs: `1px solid ${palette.neutralQuaternaryAlt}`
        },
        EntityTypeTutorial: {
            exampleStructureToggleLinkColor: palette.accent
        },
        EditableTitle: {
            fontWeight: FontWeights.semibold,
            fontSize: 24,
            lineHeight: '32px',
            height: '32px',
            marginBottom: 8
        },
        EndpointBlade: {
            textColor: palette.white,
            fontFamily: monoSpacedFont,
            backgroundColor: palette.black,
            secondaryTextColor: palette.neutralTertiaryAlt
        },
        EntityDropdown: {
            blackColor: palette.black,
            secondaryTextAlign: 'left',
            secondaryColor: palette.neutralLight,
            secondaryFontColor: palette.neutralSecondary,
            textFieldBorder: `1px ${palette.neutralQuaternaryAlt} solid`,
            menuBoxShadow: '0px 3px 3px rgba(0, 0, 0, 0.14), 0px 0px 3px rgba(0, 0, 0, 0.07)'
        },
        EntityInspector: {
            textColor: palette.white
        },
        EntityFeaturesCell: {
            fontColor: palette.neutralPrimary
        },
        EntityMigrationDoneScreen: {
            errorIconColor: palette.red
        },
        EntityDetails: {
            typeSubtitleColor: '#757372',
            textColor: palette.neutralPrimary,
            typeSubtitleFontWeight: FontWeights.regular
        },
        EntityTable: {
            newBadgeTextColor: palette.white,
            fontColor: palette.neutralPrimary,
            newBadgeColor: palette.themePrimary
        },
        EntityTreeItem: {
            ...entityColors,
            selectedBackgroundColor: NeutralColors.gray30,
            hoveredBackgroundColor: NeutralColors.gray20,
            buttonHoverColor: NeutralColors.gray30,
            entityDefaultColor: entityColors.defaultColor,
            deleteEntityButtonColor: NeutralColors.gray130,
            toggleEntityButtonColor: NeutralColors.gray130,
            addChildEntityButtonColor: NeutralColors.gray130
        },
        EntityTreeFilterBox: {
            iconColor: NeutralColors.gray130
        },
        EntityTreeTopBar: {
            titleColor: NeutralColors.gray160
        },
        EmptyState: {
            backgroundColor: '#f2f2f2',
            emptyStateLinkColor: '#006cbe'
        },
        EntityRoles: {
            primaryFontColor: palette.neutralPrimary,
            secondaryFontColor: palette.neutralSecondary
        },
        EntityUtteranceRow: {
            editInputBackgroundColor: palette.neutralLight
        },
        ExpandableCard: {
            boxShadow: effects.elevation4,
            backgroundColor: palette.white,
            headerSpacing: '11px 11px 9px 16px'
        },
        ExtractionLabeler: {
            gptIconColor: '#6F37AE',
            containerBackground: '#FFFFFF',
            rejectTagsButtonColor: 'rgba(168, 0, 0, 1)',
            acceptTagsButtonColor: 'rgba(16, 124, 16, 1)'
        },
        ExternalLink: { darkModeColor: '#7AC6FF', lightModeColor: palette.themePrimary },
        FeatureSection: {
            fontWeight: FontWeights.semibold,
            lineHeight: '26px'
        },
        Filters: {
            unclearColor: '#f2610c',
            incorrectColor: '#a80000',
            buttonMargin: spacing.s1,
            boldFontWeight: FontWeights.semibold,
            headerFontWeight: FontWeights.bold,
            headerHeight: 32
        },
        FiltersBlade: {
            primaryColor: palette.themePrimary,
            titlesColor: palette.neutralPrimary
        },
        FiltersBladeSlider: {
            sliderPadding: '0px 8px',
            sliderColor: palette.neutralSecondary,
            scoreSliderHandles: 'solid 2px #605E5C'
        },
        FiltersSection: {
            titlesColor: palette.neutralPrimary,
            titlesFontWeight: FontWeights.semibold,
            primaryColor: palette.themePrimary
        },
        FilePicker: {
            errorMessageColor: palette.red
        },
        FileViewer: {
            gptIconColor: '#6F37AE',
            borderColor: palette.neutralLight,
            sidebarBorder: `1px solid ${palette.neutralLight}`,
            fileRowBackgroundColorSelected: palette.neutralLight,
            fileRowBackgroundColorHovered: palette.neutralLighter
        },
        Footer: {
            lineHeight: '24px',
            innerSectionSpacing: '6rem 0',
            titleColor: palette.magentaDark,
            boldFontWeight: FontWeights.semibold
        },
        GlobalSubHeader: {
            rootPadding: `0 ${spacing.m}`,
            backgroundColor: palette.white,
            headerBorderColor: palette.neutralLight,
            topPanelHeight: globalSubHeaderHeight,
            activeTextFontWeight: '500',
            nonActiveTextColor: palette.neutralTertiaryAlt,
            breadcrumbTextSize: fonts.medium.fontSize,
            iconSize: fonts.small.fontSize,
            secondaryTextMargin: spacing.s1,
            fontColor: palette.black
        },
        GaugeChart: {
            color: 'FF0000'
        },
        GlobalHeaderBlades: {
            topSpacing: globalHeaderHeight,
            headerHeight: 32,
            bladePadding: '0px 16px 24px 16px',
            headerFontWeight: FontWeights.semibold,
            containerMargin: '32px'
        },
        GetStartedGuide: {
            buttonShadow: effects.elevation16,
            tableHeaderShadow: '0px -0.5px 0.3px 0.3px #b4b4b4',
            headerCollapsedColor: '#f2f2f2',
            headerExpandedColor: palette.white,
            checkMarkColor: '#0078d7'
        },
        GlobalProfileDropdown: {
            boxShadow: effects.elevation4,
            defaultTextColor: palette.black,
            secondaryTextColor: palette.neutralTertiary,
            avatarBackgroundColor: palette.themePrimary,
            signInButtonBackground: palette.neutralLighter
        },
        HeaderNavigationLink: {
            fontColor: palette.black,
            padding: `0 ${spacing.m}`,
            fontWeight: FontWeights.semibold,
            hoverBackgroundColor: palette.neutralLighter,
            selectionIndicatorColor: palette.themePrimary
        },
        HealthTokenRenderer: {
            customTokenHeight: 34,
            assertionsLineHeight: '12px',
            secondaryTextColor: '#766F6F',
            textFontFamily: monoSpacedFont,
            dropdownBackgroundColor: palette.neutralLighter
        },
        HomePageBenefits: {
            titleFontSize: '46px',
            containerPaddingLarge: '80px',
            benefitSectionPadding: '72px',
            containerPaddingSmall: spacing.l2,
            descriptionLineSpacing: spacing.l1,
            benefitSectionBorderBottom: `2px solid ${palette.neutralPrimary}`
        },
        HomePageDemo: {
            containerBackgroundColor: palette.neutralLight,
            containerPaddingLarge: '80px',
            containerPaddingSmall: spacing.l2,
            titleFontSize: '46px',
            utteranceBulletBackgroundColor: palette.neutralTertiary,
            utteranceBulletBackgroundColorHover: palette.themeLight,
            utteranceBulletBackgroundColorActive: palette.themePrimary,
            utteranceBulletSpacingPadding: spacing.m,
            utteranceBulletSpacing: spacing.l1,
            utteranceBulletBorderRadius: '2px',
            utteranceBulletTransition: 'background-color 200ms ease-in-out',
            responseContainerBackgroundColor: palette.white,
            responseContainerMaxHeight: '244px',
            responseContainerPadding: spacing.m
        },
        HomePageFooter: {
            containerBackgroundColor: palette.black,
            containerHeight: '75px',
            textColor: palette.white,
            linkMarginRight: spacing.m,
            iconFontSize: fonts.xLarge.fontSize,
            logoLinkHeight: '17px',
            separatorWidth: '1px'
        },
        HomePageHeader: {
            headerHeight: '475px',
            headerPaddingLargeScreen: '80px',
            headerPaddingSmallScreen: '40px',
            headerBackgroundColor: '#5c126b',
            textColor: palette.white,
            titleFontSizeLargeScreen: '46px',
            titleFontSizeSmallScreen: '36px',
            subtitleMarginBottom: spacing.l2,
            subTitleWidthLargeScreen: '60%',
            subTitleWidthSmallScreen: '100%',
            buttonWidth: '200px'
        },
        HomePageScenarios: {
            containerBackgroundColor: palette.neutralLight,
            containerPaddingLarge: '80px',
            containerPaddingSmall: spacing.l2,
            titleFontSize: '46px',
            scenarioSectionPadding: '72px',
            scenarioSectionBorderBottom: `2px solid ${palette.neutralPrimary}`,
            descriptionLineSpacing: spacing.l1,
            bulletBackgroundColor: palette.greenLight,
            bulletDim: '28px',
            iconWidthSmall: '40px',
            iconWidthLarge: '90px',
            linkColor: palette.themeDark
        },
        HomePageServices: {
            containerPaddingLarge: '80px',
            containerPaddingSmall: spacing.l2,
            titleFontSize: '46px'
        },
        ImportSublistsModal: {
            titleMargin: spacing.s1
        },
        InformationCard: {
            fontSize: '13px',
            borderRadius: '5px',
            cardMaxWidth: '700px',
            titleLetterSpacing: 0.3,
            largeLineHeight: '24px',
            mediumLineHeight: '20px',
            cardInnerSpacing: '12px',
            cardOuterSpacing: '4px 0',
            contentLetterSpacing: 0.5,
            textColor: palette.neutralPrimary,
            titleFontWeight: FontWeights.semibold,
            backgroundColor: palette.neutralLighterAlt
        },
        IncorrectProblem: {
            othersColor: '#767676',
            incorrectColor: '#a80000',
            fadedCorrectColor: '#E5F1FB',
            fadedUnclearColor: '#FDEFE6',
            fadedIncorrectColor: 'rgba(168, 0, 0, 0.3)'
        },
        IntentLabelingSidePane: {
            emptyTextColor: NeutralColors.gray130
        },
        IntentInspector: {
            actionFontSize: fonts.small.fontSize,
            actionColor: palette.neutralLighterAlt,
            actionHoverColor: palette.neutralLighter,
            intentsDropdownTextColor: palette.neutralTertiaryAlt
        },
        IntentPage: {
            linkColor: palette.white,
            commandBarBoxShadow: effects.elevation8
        },
        IntentPageSidePanelContainer: {
            width: '350px',
            borderSpecs: `1px solid ${palette.neutralQuaternaryAlt}`,
            titlesColor: palette.neutralPrimary,
            clearIconColor: palette.neutralSecondary
        },
        IntroSlides: {
            modalWidth: 'calc(((90vh - 43px)/ 2) * 2.73)',
            modalHeight: 'calc(90vh - 43px)',
            modalMaxWidth: '90vw',
            headerPadding: `5px ${spacing.m} 5px ${spacing.m}`,
            arrowBtnSize: fonts.xLarge.fontSize,
            navigationBtnSize: '10px',
            navigationBtnBackgroundColor: 'transparent !important',
            navigationBtnColor: '#E6E6E6',
            selectedNavigationBtnColor: '#9E9E9E',
            slidePaddingTop: '35px',
            slideHeight: '250px',
            fontWeight: 'bold',
            borderSpecs: `1px solid ${palette.neutralQuaternaryAlt}`,
            primaryPadding: spacing.m,
            primaryFontColor: palette.neutralPrimary,
            titleFontWeight: FontWeights.semibold,
            slideChildrenGap: '48px',
            slideChildrenGapXL: '0px',
            listBodyPadding: '0 80px',
            listBodyMaxWidth: '555px',
            listBodiesTextAlign: 'left',
            listBodiesFontSize: '14px',
            listBodiesChildrenGap: '30px',
            listMarginLeft: '12px',
            listItemMinWidth: '127px',
            listItemTextAlign: 'left',
            listItemMarginBottom: '25px',
            listItemFontSize: '12px',
            listItemContainerMinWidth: '127px',
            imageHeight: 'calc((90vh - 43px) / 2)'
        },
        InfoCardWithExternalLink: {
            black: palette.black,
            boxShadow: effects.elevation4,
            hoverBoxShadow: effects.elevation16,
            descriptionTextColor: palette.neutralSecondary
        },
        LanguageStudio: {
            defaultTextColor: palette.neutralPrimary,
            signedOutTitleColor: palette.themePrimary
        },
        LanguageStudioLandingPage: {
            signedOutHeaderBorderColor: palette.neutralLight
        },
        LanguageStudioOnboardingModal: {
            defaultColor: palette.white,
            slideHeaderBackgroundColor: '#5899D1',
            slideTextColor: palette.neutralSecondary,
            slidePrimaryTextColor: palette.themePrimary
        },
        LanguageStudioSignInPage: {
            footerBackgroundColor: palette.neutralLighter,
            secondaryTextColor: palette.neutralSecondaryAlt
        },
        LanguageStudioSubHeader: {
            linkColor: palette.themePrimary
        },
        LayoutManager: {
            textFontSize: '14px',
            tokenPadding: '0px 2px',
            tooltipFontSize: '12px',
            tokenBracketFontSize: '18px',
            textFontFamily: monoSpacedFont,
            tooltipLabelColor: palette.neutralTertiary,
            tooltipValueColor: palette.white
        },
        Line: {
            conflictIndicatorColor: '#EB5757',
            conflictIndicatorSize: 16
        },
        LineChart: {
            minChartWidth: '300px',
            minChartHeight: '300px',
            defaultLineColor: palette.themePrimary
        },
        LinkedEntitiesDemoPage: {
            labelColor: palette.themeDark,
            secondaryTextColor: palette.neutralSecondary
        },
        ListModal: {
            maxHeight: '40vh',
            fontSize: fonts.mediumPlus.fontSize,
            fontWeight: 500,
            letterSpacing: 0.5,
            primaryColor: palette.themePrimary,
            secondaryFontSize: fonts.smallPlus.fontSize,
            secondaryFontColor: palette.neutralTertiary
        },
        ListItems: {
            paddingLeft: '25px',
            minWidth: '200px'
        },
        LoadingPage: {
            pageBackgroundColor: palette.white,
            pageZIndex: zIndices.loadingPage
        },
        LoggedInHeader: {
            backgroundColor: palette.themePrimary,
            backgroundColorHovered: palette.themeDarkAlt,
            backgroundColorActive: palette.themeDarker,
            maxHeight: globalHeaderHeight,
            horizontalPadding: '0px 8px',
            iconPadding: '0 6px',
            textColor: palette.white,
            avatarBackgroundColor: palette.white,
            avatarForegroundColor: palette.themePrimary,
            avatarPadding: '0 2px'
        },
        LoggedOutHeader: {
            backgroundColor: palette.white,
            backgroundColorHovered: palette.neutralLight,
            backgroundColorActive: palette.neutralLight,
            horizontalPadding: '0px 20px',
            maxHeight: globalHeaderHeight,
            textColor: palette.black,
            iconPadding: '0 6px',
            logoContainerWidth: '155px',
            logoPadding: '0px 10px',
            logoLinkHeight: '93%'
        },
        LuisCommandBar: {
            borderColor: palette.neutralLight
        },
        LuisContextualMenu: {
            maxHeight: '300px !important'
        },
        LuisDropdown: {
            disabledItemColor: palette.black,
            blueColor: palette.themePrimary,
            actionItemColor: palette.themePrimary,
            searchBoxBorderColor: palette.neutralLight,
            secondaryTextColor: palette.neutralSecondary
        },
        LuisNav: {
            chevronIconColor: palette.neutralDark
        },
        LuisSearchDropdown: {
            selectedItemBackgroundColor: palette.neutralLighter,
            suffixBackgroundColor: palette.white,
            iconFontSize: fonts.small.fontSize,
            chevronColor: palette.neutralSecondary,
            secondaryTextColor: palette.neutralSecondary
        },
        LuisSidebar: {
            sidebarWidthExpanded,
            sidebarWidthCollapsed,
            sidebarBackgroundColor: palette.white,
            sidebarBorder: `1px solid ${palette.neutralLight}`,
            expandButtonColor: palette.black,
            expandButtonFontSize: fonts.xSmall.fontSize
        },
        LuisSidebarItem: {
            itemIconWidth: '48px',
            itemIconColor: palette.themePrimary,
            itemLinkHeight: '44px',
            itemChildLinkHeight: '40px',
            menuBackground: palette.white,
            itemLinkFontWeight: FontWeights.regular,
            itemLinkFontWeightSelected: FontWeights.semibold,
            itemBackgroundColorSelected: palette.neutralLight,
            itemBackgroundColorHovered: palette.neutralLighter
        },
        LuisSplitPane: {
            resizerBackgroundColor: NeutralColors.gray100
        },
        LuisStepper: {
            defaultColor: palette.accent,
            disabledStepColor: palette.neutralPrimary,
            noneStepIconColor: palette.neutralSecondaryAlt,
            inactiveSeparatorColor: palette.neutralTertiary
        },
        LuisText: { disabledColor: palette.neutralTertiaryAlt },
        LuisTooltip: {
            darkTextColor: palette.black,
            lightTextColor: palette.neutralLight,
            lightBackgroundColor: palette.white,
            darkBackgroundColor: palette.neutralPrimary
        },
        MainFiltersBlade: {
            pageSizeBtnHeight: '30px',
            primaryColor: palette.themePrimary,
            titlesColor: palette.neutralPrimary
        },
        MaintainModelPage: {
            emptyTextColor: palette.neutralSecondary
        },
        MigrationBlockerPage: {
            errorColor: palette.redDark
        },
        MigratingLuisApplicationsModal: {
            borderColor: palette.neutralQuaternaryAlt,
            focusingColor: palette.neutralQuaternaryAlt,
            hoveringColor: palette.neutralLighter,
            successColor: palette.green,
            failureColor: palette.redDark,
            warningColor: palette.yellowDark
        },
        MLEntityInfoCell: {
            addChildEntityButtonColor: NeutralColors.gray130,
            deleteEntityButtonColor: NeutralColors.gray130,
            toggleEntityButtonColor: NeutralColors.gray130,
            buttonHoverColor: NeutralColors.gray30
        },
        ModelConfusionMatrix: {
            tpCellTextColor: palette.white,
            mainCellBackgroundColor: '#B7C9D7',
            tpCellBackgroundColor: palette.themeDark,
            predictionBorderBottom: '1px solid #F1F1F1'
        },
        ModelDetailsCard: {
            boxShadow: effects.elevation4,
            trainingInProgressColor: palette.themePrimary,
            secondaryTextColor: palette.neutralTertiary,
            borderColor: palette.neutralTertiaryAlt,
            precision: '#008575',
            recall: '#0078D4',
            f1Score: '#8661C5',
            neutral: palette.neutralQuaternaryAlt
        },
        ModelDetailsListTable: {
            textColor: palette.neutralPrimary,
            completedColor: palette.green,
            failedColor: palette.red,
            warningIconColor: '#E8825D',
            errorIconColor: palette.redDark,
            expiryIconLeftMargin: '12px',
            expiryIconRightMargin: '3px'
        },
        SummarizationModelDetailsListTable: {
            textColor: palette.neutralPrimary,
            completedColor: palette.green,
            failedColor: palette.red,
            warningIconColor: '#E8825D',
            errorIconColor: palette.redDark,
            expiryIconLeftMargin: '12px',
            expiryIconRightMargin: '3px'
        },
        ModelList: {
            correctColor: palette.green,
            height: '400px',
            failColor: palette.red,
            border: `1px solid ${palette.neutralQuaternaryAlt}`
        },
        ModelTrainingLayout: {
            footerBorderColor: palette.neutralQuaternaryAlt
        },
        NameCellLink: {
            fontSize: fonts.mediumPlus.fontSize
        },
        NameRow: {
            fontSize: fonts.mediumPlus.fontSize,
            fontWeight: 500,
            letterSpacing: 0.5,
            primaryColor: palette.themePrimary,
            secondaryFontSize: fonts.smallPlus.fontSize,
            secondaryFontColor: palette.neutralTertiary
        },
        NewUtteranceRow: {
            labelerMargin: '8px 6px 8px 0px',
            editUtteranceInputHeight: '40px',
            editInputBackgroundColor: palette.neutralLight
        },
        NotFoundPage: {
            textMargin: spacing.m
        },
        NotificationsIcon: {
            badgeTinyFontSize: '9px',
            progressBarColor: palette.white,
            badgeBackgroundColor: palette.white,
            badgeFontSize: fonts.xSmall.fontSize,
            badgeFontWeight: FontWeights.semibold,
            badgeForegroundColor: palette.themePrimary
        },
        OrchestrationDeployModelModal: {
            borderColor: palette.neutralQuaternaryAlt
        },
        OverlapMethodCard: {
            statusBackground: '#018A39',
            statusTextColor: palette.white,
            inprogressStatusColor: palette.neutralLight,
            cardBosShadow: '0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
        },
        Page: {
            containerPadding: '3rem 10%',
            containerFluidPadding: `10px ${spacing.l2} ${spacing.l2} ${spacing.l2}`
        },
        PatternInput: {
            fontSize: '15px',
            lineHeight: '24px',
            letterSpacing: '-0.5px'
        },
        PhraseListModal: {
            actionButtonHeight: '24px'
        },
        PatternsPage: {
            textColor: palette.neutralPrimary
        },
        PatternRow: {
            entityLineHeight: '24px',
            entityPadding: '4px 2px',
            entityBorderRadius: '2px',
            fontColor: palette.neutralPrimary,
            entityFontWeight: FontWeights.semibold,
            entityBackgroundColor: palette.themeLight
        },
        PhraseListTable: {
            usedModelTypeColor: palette.neutralSecondary
        },
        PhraseListTagsContainer: {
            containerPadding: spacing.s1,
            containerBorder: `1px solid ${palette.neutralQuaternary}`,
            containerBorderRadius: effects.roundedCorner2,
            containerLegendPadding: spacing.s1,
            containerHeight: '120px',
            requiredIndicatorColor: palette.redDark
        },
        PhraseListTagsRenderer: {
            iconFontSize: fonts.smallPlus.fontSize,
            iconRotation: 'rotate(45deg)'
        },
        Pill: {
            iconPaddingLeft: '1px',
            iconFontSize: fonts.smallPlus.fontSize,
            boxSizing: 'content-box',
            flexShrink: '1',
            height: '26px',
            closeIconHeight: '24px',
            lineHeight: '26px',
            display: 'flex',
            flexWrap: 'nowrap',
            maxWidth: '100px',
            marginLeft: '2px',
            padding: '6px',
            calloutLeftAdjust: '-7px',
            calloutTopAdjust: '-2px',
            calloutWidth: '170px',
            fontSize: fonts.smallPlus.fontSize,
            borderColor: NeutralColors.gray60,
            removeIconHoverBackgroundColor: NeutralColors.gray40,
            removeIconActiveBackgroundColor: NeutralColors.gray50,
            removeIconActiveColor: palette.neutralPrimary
        },
        PillContainer: {
            padding: spacing.s2,
            iconDimension: '26px'
        },
        PrebuiltDemoCard: {
            linkColor: palette.white,
            boxShadow: effects.elevation4,
            boxShadowHover: effects.elevation16,
            greyCardColor: palette.neutralLighter,
            secondaryTextColor: palette.neutralSecondary,
            hoverBackgroundColor: palette.neutralLighterAlt
        },
        PrebuiltEntityRow: {
            nameFontWeight: FontWeights.semibold
        },
        PredictionResourcesPivot: {
            sectionPaddingTop: '25px',
            shimmerContainerWidth: '90%',
            fullWidthContainerWidth: '100%',
            emptyPlaceholderColor: palette.neutralSecondary
        },
        PrepareContainerModal: {
            defaultTextColor: palette.black
        },
        ProjectCreationModalBase: {
            defaultTextColor: palette.black,
            borderColor: palette.neutralQuaternaryAlt
        },
        ProjectCreationConnectStorage: {
            warningColor: '#D83B01',
            successColor: palette.green,
            pendingColor: palette.neutralPrimary,
            disableColor: palette.neutralTertiaryAlt
        },
        getAmlWorkSpaces: {
            warningColor: '#D83B01',
            successColor: palette.green,
            pendingColor: palette.neutralPrimary,
            disableColor: palette.neutralTertiaryAlt
        },
        ProjectCreationModalBasicInfo: {
            calloutLinkColor: '#009eff'
        },
        ProjectsCommandBar: {
            defaultTextColor: palette.black,
            topBorderColor: palette.neutralLighter,
            secondaryColor: palette.neutralSecondary,
            bottomBorderColor: palette.neutralLighter
        },
        ProjectsCustomizationSections: {
            sectionBorderColor: palette.neutralLight,
            linkHoverColor: palette.neutralLighter
        },
        PublishSettingsPage: {
            subtitleLineHeight: '20px'
        },
        PublishSlotCard: {
            settingWidth: '150px',
            publishDateFontColor: palette.neutralSecondary
        },
        PublishPanelModal: {
            optionPaddingLeft: spacing.l2,
            optionMarginBottom: spacing.l1,
            optionTextTopAdjust: '3px'
        },
        QnaManageSourcesCommandBar: {
            topBorderColor: palette.neutralLighter,
            defaultTextColor: palette.black
        },
        QnaMakerEditPage: {
            borderColor: `1px solid ${palette.neutralLight}`,
            collapseIconColor: palette.black
        },
        QnaMakerProjectDeployedCard: {
            completedIconColor: palette.green,
            failedIconColor: palette.red,
            titleTextColor: palette.neutralSecondary
        },
        QnaMakerFiltersBlade: {
            borderSpecs: `1px solid ${palette.neutralQuaternaryAlt}`,
            iconColor: palette.neutralSecondary
        },
        QnaMakerLeftSidePane: {
            sidebarBorder: `1px solid ${palette.neutralLight}`,
            collapseIconColor: palette.black
        },
        QnaMakerSidebar: {
            yellowDotColor: '#ffaa44',
            itemLinkFontWeight: FontWeights.regular,
            itemLinkFontWeightSelected: FontWeights.semibold
        },
        QnaMakerAnswerColumnRenderer: {
            followUpPromptTitleColor: palette.neutralSecondary,
            promptItemBackgroundColor: palette.white,
            promptItemDisabledColor: palette.neutralTertiary,
            AddButtonColor: palette.themePrimary,
            actionBtnColor: palette.themePrimary,
            textColor: palette.neutralPrimary
        },
        QnaMakerAzureSearchCard: {
            labelColor: '#727272',
            iconColor: 'green'
        },
        QnaMakerAzureSearchCommandBar: {
            topBorderColor: palette.neutralLighter,
            actionButtonColor: '#0078d4'
        },
        QnaMakerBreadcrumb: {
            linkColor: palette.themePrimary
        },
        QnaMakerChatTestBlade: {
            disabledColor: palette.neutralTertiary,
            messageBorderColor: palette.black,
            defaultBorderColor: palette.neutralTertiary
        },
        QnaMakerContextualTreeItem: {
            secondaryColor: palette.themePrimary
        },
        QnaMakerFollowUpPromptModal: {
            pairHoveringBackground: palette.neutralLight
        },
        QnaMakerQnaCard: {
            bottomBorder: `1px solid ${palette.neutralLight}`,
            hoveringColor: palette.neutralLighter,
            focussingColor: palette.neutralQuaternaryAlt
        },
        QnaMakerQnaCommandBar: {
            disabledTextColor: palette.neutralTertiary
        },
        QnaMakerQnaPairDetails: {
            buttonColor: palette.themePrimary,
            textColor: palette.neutralPrimary,
            disabledLabelColor: palette.neutralTertiary,
            descriptionColor: '#605E5C'
        },
        QnaMakerQnaRowRenderer: {
            secondaryColor: palette.neutralSecondary,
            borderColor: palette.themeSecondary,
            hoveringColor: palette.neutralLighter,
            focussingColor: palette.neutralQuaternaryAlt
        },
        QnaMakerQuestionsColumnRenderer: {
            actionBtnColor: palette.themePrimary,
            textColor: palette.neutralPrimary,
            suggestedQuestionBackgroundColor: palette.white
        },
        QnaMakerSynonymCard: {
            bottomBorder: `1px solid ${palette.neutralLight}`,
            hoveringColor: palette.neutralLighter,
            focussingColor: palette.neutralQuaternaryAlt
        },
        QnaMakerSynonymRowRenderer: {
            secondaryColor: palette.neutralSecondary,
            borderColor: palette.themeSecondary,
            hoveringColor: palette.neutralLighter,
            focussingColor: palette.neutralQuaternaryAlt
        },
        QuestionAnswersDemoPage: {
            answerLabelColor: '#F2610C',
            requiredColor: palette.redDark,
            secondaryTextColor: palette.neutralSecondary
        },
        Renderers: {
            ...entityColors,
            fontFamily: fonts.medium.fontFamily
        },
        ResourceKeyCell: {
            shimmerMiddleColor: palette.white,
            shimmerEdgeColor: palette.neutralLighter
        },
        ReviewVerificationSetCommandBar: {
            disabledTextColor: palette.neutralTertiary
        },
        SampleButton: {
            boxShadow: effects.elevation8,
            hoverBoxShadow: effects.elevation16,
            border: '2px solid rgb(0, 120, 212)'
        },
        SamplingTrackers: {
            textMargin: spacing.s1
        },
        SamplingCommandBar: {
            borderColor: palette.neutralLight,
            backgroundColor: palette.white
        },
        SamplingFooter: {
            footerBorderColor: palette.neutralQuaternaryAlt
        },
        SentimentDemoPage: {
            labelColor: palette.themeDark,
            boxShadow: effects.elevation16,
            positiveSentimentColor: '#96C13C',
            negativeSentimentColor: palette.redDark,
            neutralSentimentColor: palette.themePrimary
        },
        SessionExpiredPage: {
            textMargin: spacing.m
        },
        ShimmerFactory: {
            containerWidth: '100%'
        },
        SkipToMainContentLink: {
            lightThemeBackgroundColor: palette.white,
            darkThemeBackgroundColor: palette.black,
            darkThemeTextColor: palette.white,
            lightThemeTextColor: palette.black,
            zIndex: zIndices.skipLink,
            padding: '11px 0px'
        },
        StackedBarChart: {
            thirdColor: '#a80000',
            secondColor: '#f2610c',
            firstColor: palette.themePrimary
        },
        StackBladeHost: {
            panelPadding: '0 !important',
            defaultBladeWidth: 400,
            defaultTopSpacing: 0
        },
        SubmitTrainingJobPanel: {
            footerBorderColor: palette.neutralQuaternaryAlt
        },
        SuggestTableToolbar: {
            marginTop: '10px',
            marginLeft: '28px'
        },
        SavingIndicator: {
            btnColor: palette.themePrimary,
            dirtyColor: palette.yellowDark,
            inProgressColor: palette.yellowDark,
            syncedColor: palette.green
        },
        SummarizationProjectsTable: {
            projectTypeIconColor: palette.themePrimary
        },
        SummarizationBreadcrumb: {
            linkColor: palette.themePrimary
        },
        SummarizationTestModelPage: {
            titleLineHeight: '56px',
            titleFontWeight: FontWeights.semibold,
            topBorderColor: palette.neutralLighter
        },
        SummarizationDocumentSidePane: {
            gptIconColor: '#6F37AE',
            borderColor: palette.neutralQuaternaryAlt
        },
        SummarizationDocumentView: {
            borderColor: palette.neutralQuaternaryAlt
        },
        Table: {
            tableWrapperMinHeight: '50px',
            emptyPlaceholderMargin: spacing.s1,
            emptyPlaceholderFontWeight: FontWeights.regular,
            headerCellNameColor: palette.neutralPrimary,
            headerCellNameFontWeight: FontWeights.semibold
        },
        TablePagination: {
            containerMargin: spacing.l2,
            pageMargin: `0 ${spacing.s1}`,
            pageLinkPadding: '0 12px',
            pageLinkBackgroundColor: palette.white,
            pageLinkHoverBackgroundColor: palette.neutralLighter,
            pageLinkDisabledColor: palette.neutralTertiary,
            pageLinkDisabledBackgroundColor: palette.neutralLighter,
            pageLinkBorder: `1px solid ${palette.black}`,
            pageLinkDisabledBorder: `1px solid ${palette.neutralLighter}`,
            pageLinkBorderRadius: effects.roundedCorner2,
            pageLinkHeight: '32px',
            pageLinkLineHeight: '32px',
            pageLinkFontSize: fonts.small.fontSize,
            pageActiveLinkBackgroundColor: palette.themePrimary,
            pageActiveLinkColor: palette.white
        },
        TagsCountTable: {
            warningIconColor: '#E8825D'
        },
        TagsCountBarChart: {
            barColor: '#418DED',
            labelColor: '#646464'
        },
        TagUtterancesPageSidePaneContainer: {
            borderSpecs: `1px solid ${palette.neutralQuaternaryAlt}`,
            iconColor: palette.neutralSecondary
        },
        TestBladeHeader: {
            headerFontWeight: FontWeights.bold,
            headerFontSize: fonts.large.fontSize
        },
        TestDocumentDetailsModal: {
            warningIconColor: '#F2610C',
            completedIconColor: '#018A39',
            defaultTextColor: palette.black,
            borderColor: palette.neutralQuaternaryAlt
        },
        TestPaneEntityPrediction: {
            titleColor: palette.neutralQuaternaryAlt,
            highlightedTextColor: palette.themeTertiary,
            informativeTextColor: palette.neutralTertiary
        },
        TestPaneEntityTree: {
            highlightedTextColor: palette.themeTertiary,
            expandingButtonDefaultColor: palette.neutralQuaternary
        },
        TestPaneSeparator: {
            mainSeparatorColor: palette.white,
            secondarySeparatorColor: palette.neutralSecondary,
            headerSeparatorColor: palette.neutralQuaternaryAlt
        },
        TestPaneTopAction: {
            actionFontSize: fonts.small.fontSize,
            actionColorLight: palette.neutralLighterAlt,
            actionColorDark: palette.themePrimary
        },

        TitledSection: {
            titleFontWeight: FontWeights.regular,
            containerMarginBottom: '48px',
            calloutTopAdjust: '2px'
        },
        Tile: {
            backgroundColor: palette.white,
            boxShadow: effects.elevation16,
            borderRadius: '3px'
        },
        TileHeader: {
            borderRadius: '3px',
            borderBottom: `1px solid ${palette.neutralQuaternary}`
        },
        TileFooter: {
            tileHeight: '60px',
            tileBorderTop: `1px solid ${palette.neutralQuaternary}`,
            tileMarginTop: 'auto'
        },
        Toast: {
            floatingContainerWidth: '360px',
            floatingContainerMargin: spacing.s1,
            floatingContainerPadding: spacing.s1,
            floatingContainerBoxShadow: effects.elevation16,
            dismissIconHeight: '16px',
            dismissIconWidth: '16px',
            dateFontColor: palette.neutralSecondary,
            contentFontColor: palette.neutralPrimaryAlt,
            dateMarginRight: spacing.l1,
            toastBackgroundColor: palette.white,
            inProgressBackgroundColor: palette.neutralLighterAlt,
            successBackgroundColor: '#DFF6DD',
            failureBackgroundColor: '#FDE7E9',
            successColor: palette.green,
            errorColor: palette.red,
            progressColor: palette.accent,
            toastTitleLineHeight: '18px',
            toastTitleMarginBottom: spacing.s1,
            contentFontSize: fonts.medium.fontSize,
            contentFontFamily: fonts.medium.fontFamily,
            contentMarginLeft: '22px',
            contentMarginBottom: '8px',
            toastBorder: `1px solid ${palette.neutralQuaternary}`,
            toastButtonHeight: '24px',
            iconMarginRight: spacing.s1
        },
        Toaster: {
            containerZIndex: zIndices.toaster,
            containerTop: '84px',
            containerPadding: spacing.s1
        },
        ToastPanel: {
            toastPanelContainerPadding: spacing.s1,
            dropDownActionColor: palette.accent,
            dropDownActionWidth: '16px',
            actionBarBorderBottom: `1px solid ${palette.neutralQuaternary}`,
            noToastsMessageMarginTop: spacing.l1,
            noToastsMessageFontWeight: '600'
        },
        TokenRenderer: {
            bracketFontFamily: fonts.medium.fontFamily,
            bracketFontSize: '24px',
            bracketFontWeight: 500,
            tokenBoldFont: FontWeights.semibold,
            tokenRegularFont: FontWeights.regular,
            selectedTokenBackground: 'rgba(105, 175, 229, 0.3)',
            conflictTokenBackground: 'rgba(235, 87, 87, 0.5) '
        },
        TrainingLimitsUI: {
            successColor: palette.green,
            headerColor: palette.neutralLighter,
            separatorColor: palette.neutralLight
        },
        TrainingJobTable: {
            successColor: palette.green,
            failureColor: palette.redDark,
            cancelColor: palette.yellowDark
        },
        TranslationBreadcrumb: {
            linkColor: palette.themePrimary
        },
        TranslationToolFooter: {
            backgroundColor: palette.white
        },
        TranslationHistoryPage: {
            successColor: palette.green,
            failureColor: palette.redDark,
            warningColor: palette.yellowDark,
            disabledIconBtnColor: `inherit !important`,
            groupBorder: `1px solid ${palette.neutralLighter}`,
            rowBackgroundColor: 'transparent !important'
        },
        TranslationSettingsPage: {
            border: `1px solid ${palette.neutralQuaternaryAlt}`
        },
        TranslationLocalFileTable: {
            borderColor: palette.neutralLight,
            requiredColor: palette.redDark
        },
        TranslationStorageFileTable: {
            fileIconColor: palette.blue,
            folderIconColor: palette.yellow,
            linkColor: palette.themePrimary,
            borderColor: palette.neutralLight
        },
        TranslationCreationPage: {
            secondaryTextColor: palette.neutralSecondary,
            defaultTextColor: palette.black,
            borderColor: palette.neutralLight
        },
        TranslationQuotaConsumptionCard: {
            cardBackgroundColor: 'rgb(243, 242, 241)'
        },
        TranslationSavingIndicator: {
            dirtyColor: palette.yellowDark,
            inProgressColor: palette.yellowDark,
            syncedColor: palette.green
        },
        TranslationConfigurationPage: {
            defaultTextColor: palette.black,
            secondaryTextColor: palette.neutralSecondary
        },
        TranslationFilesSelectionPage: {
            requiredColor: palette.redDark
        },
        OverviewTab: {
            emptyStateLinkColor: '#006cbe'
        },
        UnclearProblem: {
            othersColor: '#767676',
            unclearColor: '#F2610C',
            fadedCorrectColor: '#E5F1FB',
            fadedUnclearColor: 'rgba(242, 97, 12, 0.5)',
            fadedIncorrectColor: 'rgba(168, 0, 0, 0.1)'
        },
        UtteranceInspector: {
            padding: spacing.m,
            textColor: palette.white,
            backgroundColor: palette.neutralPrimaryAlt,
            utteranceTextColor: palette.neutralTertiaryAlt
        },
        UtteranceTable: {
            minRowHeight: '55px',
            unclearColor: '#f2610c',
            incorrectColor: '#a80000',
            inputBorder: palette.neutralSecondary,
            untrainedBorder: '1px solid',
            boldFont: FontWeights.semibold,
            regularFont: FontWeights.regular,
            addUtteranceInputHeight: '40px',
            headerWrapperMinRowHeight: '48px',
            headerWrapperOuterSpacing: '32px',
            headerWrapperInnerSpacing: '16px 32px 9px 58px',
            addUtteranceInputBorder: '0px solid transparent',
            headerWrapperBorder: '1px solid rgb(244, 244, 244)'
        },
        UtteranceTableContextualMenu: {
            iconColor: palette.themeDarker
        },
        VisualizedCluResults: {
            answerColor: '#F2610C',
            borderColor: palette.neutralLight,
            secondaryTextColor: palette.neutralSecondary
        },
        VisualizedCustomClassificationResult: { defaultCardColor: palette.themePrimary }
    };
};

export type AppTheme = ReturnType<typeof buildTheme>;
